/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: Slobodan Djordjevic
 */
import React, { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory, useLocation } from "react-router"
import {
    Modal,
    ModalTitle,
    TitleSection,
    ContentSection,
    Row,
    Label,
    Chip,
    pageTitle,
    Button,
    FooterSection,
    isSmallMobileScreen,
} from "trux"
import localforage from "localforage"
import { TRCToggle } from "../../trace"

import "./SettingsModal.css"
import * as links from "../../util/links"
import localForage from "localforage"

const SPEED_VALUES = [
    { id: 0, value: 0.5, text: ".5x" },
    { id: 1, value: 1, text: "1x" },
    { id: 2, value: 1.5, text: "1.5x" },
    { id: 3, value: 2, text: "2x" },
]

export const SettingsModal = () => {
    const dispatch = useDispatch()
    const location = useLocation()
    const history = useHistory()

    const [speed, setSpeed] = useState(1)
    const [muted, setMuted] = useState(true)
    const [loopMomentPlay, setLoopMomentPlay] = useState(true)
    const [zoomEnabled, setZoomEnabled] = useState(isSmallMobileScreen())
    const [open, setOpen] = useState(false)
    const videoInfo = useSelector((state) => state.video.value.info)
    const [videoPaused, setVideoPaused] = useState(false)

    let video = document.getElementById("video")
    if (video && open) {
        let pauseEvent = new CustomEvent("trc-pause")
        document.dispatchEvent(pauseEvent)
    }

    const close = (e) => {
        if (e) {
            e.preventDefault()
        }
        history.push(location.pathname.replace("/settings", ""))
        if (!videoPaused) {
            let playEvent = new CustomEvent("trc-play")
            document.dispatchEvent(playEvent)
        }
        setOpen(false)
    }

    const confirmSettings = (e) => {
        let settings = {
            speed,
            muted,
            loopMomentPlay,
            zoomEnabled,
            videoPaused,
        }

        localForage.setItem(`video_settings`, settings).then(() => {
            dispatch({ type: "SETTINGS_SET", video_settings: settings })
        })

        close(e)
    }

    useEffect(() => {
        localforage.getItem(`video_settings`).then(function (settings) {
            if (settings) {
                setSpeed(settings.speed)
                setMuted(settings.muted)
                setLoopMomentPlay(settings.loopMomentPlay)
                setZoomEnabled(settings.zoomEnabled)
            }
            // We need this to be able to pause video after closing settings dialog, if video was paused before dialog opening
            setVideoPaused(videoInfo.paused)
            setOpen(links.getUrlExtras(window.location.hash).settings !== null)
        })
    }, [open])

    useEffect(() => {
        if (open) {
            pageTitle("Settings", "Settings")
        }
    }, [open])

    useEffect(() => {
        window.addEventListener("resize", handleOrientationChange)

        return () => {
            window.removeEventListener("resize", handleOrientationChange)
        }
    }, [])

    const handleOrientationChange = () => {
        localforage.getItem(`video_settings`).then(function (settings) {
            if (!settings) {
                setZoomEnabled(isSmallMobileScreen())
            }
        })
    }

    const selectSpeed = (value) => {
        setSpeed(value)
    }

    const toggleMuted = () => {
        setMuted(!muted)
    }

    const toggleLoopMomentPlay = () => {
        setLoopMomentPlay(!loopMomentPlay)
    }

    const toggleZoom = () => {
        setZoomEnabled(!zoomEnabled)
    }

    return (
        <Modal
            trkContext="Video Settings"
            open={open}
            onShadeClick={close}
            className="settings-modal"
        >
            <TitleSection onCloseClick={close}>
                <ModalTitle content="Settings" />
            </TitleSection>
            <ContentSection>
                <Row center marginBottom="2">
                    <Label
                        type={Label.Type.Heading5}
                        bold
                        color="grey1-color"
                        content={"Playback speed"}
                    />
                </Row>
                <Row justifyContent="center" marginBottom="8">
                    {SPEED_VALUES.map((item) => {
                        return (
                            <Chip
                                key={item.id}
                                background={
                                    speed === item.value
                                        ? "black-color"
                                        : "grey4-color"
                                }
                                color={
                                    speed === item.value
                                        ? "white2-color"
                                        : "black-color"
                                }
                                content={item.text}
                                trkAction={`Speed ${item.text}`}
                                onClick={() => selectSpeed(item.value)}
                                fat
                                marginLeft="1"
                                marginRight="1"
                            />
                        )
                    })}
                </Row>

                <Row center marginBottom="2">
                    <Label
                        type={Label.Type.Heading5}
                        bold
                        color="grey1-color"
                        content={"Game sound"}
                    />
                </Row>
                <Row center marginBottom="8">
                    <TRCToggle
                        trkAction="Muted Toggled"
                        checked={!muted}
                        onToggle={() => toggleMuted()}
                    />
                </Row>

                <Row center marginBottom="2">
                    <Label
                        type={Label.Type.Heading5}
                        bold
                        color="grey1-color"
                        content={"Loop highlight play"}
                    />
                </Row>
                <Row center marginBottom="8">
                    <TRCToggle
                        trkAction="Loop Toggled"
                        checked={loopMomentPlay}
                        onToggle={() => toggleLoopMomentPlay()}
                    />
                </Row>

                <Row center marginBottom="2">
                    <Label
                        type={Label.Type.Heading5}
                        bold
                        color="grey1-color"
                        content={"Zoom camera"}
                    />
                </Row>
                <Row center>
                    <TRCToggle
                        trkAction="Zoom Toggled"
                        checked={zoomEnabled}
                        onToggle={() => toggleZoom()}
                    />
                </Row>
            </ContentSection>

            <FooterSection>
                <Button
                    trkAction="Confirm Pressed"
                    content="Confirm settings"
                    onClick={(e) => confirmSettings(e)}
                    primary
                />
            </FooterSection>
        </Modal>
    )
}
